import React from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollToTop } from '../utils/scrollToTop';

const About = () => {
  const navigate = useNavigate();

  const handleGetStartedClick = () => {
    navigate('/register');
    scrollToTop();
  };

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Excellence in Sports",
      description: "Promoting cricket and youth development through structured training and competitive tournaments."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Community Unity",
      description: "Building strong bonds and lasting friendships that unite people of all ages in Kabir Nagar."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Future Champions",
      description: "Nurturing talent and inspiring healthy, active lifestyles to create tomorrow's leaders."
    }
  ];

  return (
    <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            About <span className="text-orange-600">KNKSS</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
          <p className="section-subtitle max-w-4xl mx-auto">
            The heart of sports and community in Raipur
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-16">
          {/* Left Column - Text */}
          <div className="space-y-8 flex flex-col justify-center h-full">
            <div className="space-y-6">
              <p className="text-xl text-gray-700 font-medium text-left">
                Kabir Nagar Khel Samiti Society (KNKSS), Raipur, stands as a beacon of sports excellence and community development in Chhattisgarh. Established with a vision to transform lives through sports, we are dedicated to promoting the overall well-being and holistic development of young individuals through active participation in sports and physical activities.
              </p>
              <p className="text-xl text-gray-700 font-medium text-left">
                Our organization serves as a cornerstone for sports development in the community, fostering growth, unity, and excellence in athletics while building character, leadership, and life skills that extend far beyond the playing field.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">15+</div>
                <div className="text-sm sm:text-base text-gray-600 font-medium">Members</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">25+</div>
                <div className="text-sm sm:text-base text-gray-600 font-medium">Events</div>
              </div>
              <div className="text-center col-span-2 sm:col-span-1">
                <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">8+</div>
                <div className="text-sm sm:text-base text-gray-600 font-medium">Years</div>
              </div>
            </div>
          </div>

          {/* Right Column - Single Image */}
          <div className="relative flex justify-center items-start h-full">
            <div className="w-full h-80 rounded-2xl overflow-hidden shadow-2xl max-w-sm relative group border-2 border-orange-500">
              {/* Main Image */}
              <img 
                src="/images/img.10.jpg" 
                  alt="KNKSS Team"
                className="w-full h-full object-cover object-center"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.className = 'w-full h-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center';
                  placeholder.innerHTML = `
                    <div class="text-center">
                      <svg class="w-16 h-16 mx-auto mb-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                      </svg>
                      <p class="text-orange-600 font-semibold">KNKSS Team</p>
                      <p class="text-orange-500 text-sm">Image Unavailable</p>
                    </div>
                  `;
                  e.target.parentNode.appendChild(placeholder);
                }}
              />
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-orange-500 to-red-600 rounded-full opacity-20 z-10"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-20"></div>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <div key={index} className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-full mb-6 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="card-title text-gray-900 mb-4">
                {feature.title}
              </h3>
              <p className="text-body text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 sm:mt-20">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 sm:p-12 text-white">
            <h3 className="text-h3 mb-4">
              Ready to Join Our Community?
            </h3>
            <p className="text-body mb-8 opacity-90">
              Be part of our journey toward excellence and unity in sports
            </p>
            <button 
              onClick={handleGetStartedClick}
              className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg btn-text-lg"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;